/**
 * Get registry configuration (operator, resolver, treasury)
 */
import type { Address } from "viem"
import { readContract } from "viem/actions"
import { breakoutRegistryAbi } from "../../abi/generated/breakoutRegistry"
import type { ReadableClient } from "../../internal/contracts"

export interface GetRegistryConfigParams {
	registry: Address
}

export interface RegistryConfig {
	operator: Address
	resolver: Address
	treasury: Address
}

/**
 * Get the registry configuration addresses.
 *
 * @returns Registry config with operator, resolver, and treasury addresses
 *
 * @example
 * ```ts
 * import { getRegistryConfig } from "@breakout/sdk/actions/read"
 *
 * const config = await getRegistryConfig(publicClient, {
 *   registry: "0x...",
 * })
 * console.log(config.operator) // "0x..."
 * console.log(config.resolver) // "0x..."
 * console.log(config.treasury) // "0x..."
 * ```
 */
export async function getRegistryConfig(
	client: ReadableClient,
	params: GetRegistryConfigParams,
): Promise<RegistryConfig> {
	const { registry } = params

	const [operator, resolver, treasury] = await Promise.all([
		readContract(client, {
			address: registry,
			abi: breakoutRegistryAbi,
			functionName: "operator",
		}),
		readContract(client, {
			address: registry,
			abi: breakoutRegistryAbi,
			functionName: "resolver",
		}),
		readContract(client, {
			address: registry,
			abi: breakoutRegistryAbi,
			functionName: "treasury",
		}),
	])

	return {
		operator,
		resolver,
		treasury,
	}
}
